import { apiHideMultipleAnnouncementPopup } from "@app/api/announcements/announcements.api";
import { DataDialog } from "@app/components/atoms";
import { formatAnnouncementsBody, isValidUrl } from "@app/helpers";
import { useTranslation } from "react-i18next";
import { Button as MuiButton } from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { Box, CircularProgress } from "@mui/material";
import { Announcements } from "@app/types";
import { useState, useRef } from "react";

interface AnnouncementPopupModalProps {
  announcements?: Announcements[];
  announcementsLoading: boolean;
  openAnnouncements: boolean;
  setOpenAnnouncements: React.Dispatch<React.SetStateAction<boolean>>;
  isoLocaleCode: string;
  userTimeFormat: string;
}

export function AnnouncementPopupModal({
  announcements,
  announcementsLoading,
  openAnnouncements,
  setOpenAnnouncements,
  isoLocaleCode,
  userTimeFormat,
}: AnnouncementPopupModalProps) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [dontShowAgainIds, setDontShowAgainIds] = useState<string[]>([]);

  const handleScroll = () => {
    const element = carouselRef.current;
    if (element) {
      const newIndex = Math.round(element.scrollLeft / element.offsetWidth);
      setCurrentIndex(newIndex);
    }
  };

  const scrollToIndex = (index: number) => {
    const element = carouselRef.current;
    if (element) {
      const width = element.offsetWidth;
      element.scrollTo({ left: index * width, behavior: "smooth" });
      setCurrentIndex(index);
    }
  };

  const handleCloseAnnouncementMenu = async (announcementId: string) => {
    if (!announcements) return;
    const id = announcementId;

    const updatedIds = [...dontShowAgainIds, id];
    setDontShowAgainIds(updatedIds);

    if ((announcements?.length ?? 0) === 1) {
      await apiHideMultipleAnnouncementPopup(updatedIds);
      setOpenAnnouncements(false);
      return;
    }

    const nextIndex = currentIndex + 1;

    if (nextIndex < announcements.length) {
      scrollToIndex(nextIndex);
    } else {
      await apiHideMultipleAnnouncementPopup(updatedIds);
      setOpenAnnouncements(false);
    }
  };

  return (
    <DataDialog
      disableBackdropClick={true}
      title={
        <>
          <CampaignOutlinedIcon sx={{ fontSize: 30 }} /> {t("announcements")}
        </>
      }
      open={openAnnouncements}
      setOpen={setOpenAnnouncements}
      dialogActions={
        <div className="flex align-items-center justify-content-end"></div>
      }
      minimizeSpacing
    >
      <div className="announcement-carousel-wrapper mt-5">
        {announcementsLoading ? (
          <div className="announcement-loading">
            <CircularProgress size={40} sx={{ color: "#140a9a" }} />
          </div>
        ) : announcements && announcements?.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-evenly",
                alignItems: "center",
              }}
            >
              <div
                className="announcement-carousel"
                ref={carouselRef}
                onScroll={handleScroll}
              >
                {announcements.map((announcement, index) => (
                  <div className="announcement-slide" key={index}>
                    <div className="announcement-card">
                      <div className="announcement-header">
                        <p className="announcement-title">
                          {announcement.title}
                        </p>
                      </div>
                      <p
                        className="notification-body"
                        style={{ whiteSpace: "pre-wrap" }}
                      >
                        {(() => {
                          const body = announcement?.body || "";
                          const localeDateStart = t(
                            "announcementDateStartMatch",
                          );
                          const localeDateEnd = t("announcementDateEndMatch");
                          const localeDateNotice = t(
                            "announcementDateNoticeMatch",
                          );
                          const { formattedBody: updatedBody, url } =
                            formatAnnouncementsBody(
                              body,
                              localeDateStart,
                              localeDateEnd,
                              localeDateNotice,
                              isoLocaleCode,
                              userTimeFormat,
                            );

                          if (!url) return updatedBody;
                          const parts = updatedBody.split(url);

                          return (
                            <>
                              {parts[0]}
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="notification-link"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {url}
                              </a>
                              {parts[1]}
                            </>
                          );
                        })()}
                      </p>
                      {announcement.contentData.link &&
                        isValidUrl(announcement.contentData.link) && (
                          <p className="announcement-body">
                            <a
                              href={announcement.contentData.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="announcement-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {announcement.contentData.link}
                            </a>
                          </p>
                        )}
                      <div className="announcement-checkbox justify-content-end">
                        <div className="flex align-items-center justify-content-end">
                          <MuiButton
                            onClick={() =>
                              handleCloseAnnouncementMenu(
                                announcement.contentData.announcementId,
                              )
                            }
                            className="btn btn-primary"
                            style={{ width: "4rem" }}
                          >
                            {t("ok")}
                          </MuiButton>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Box>
          </>
        ) : (
          <p className="fs-16 txt-gray-1 m-0">{t("noAnnouncementsFound")}</p>
        )}
      </div>
    </DataDialog>
  );
}
